/**
 * The ONE typed client for the Carno platform API (docs/API.md §2 plus the
 * routes agreed for the shell). Every request from the shell goes through
 * here, so contract drift is fixed in one place.
 *
 * Rules enforced here:
 * - same-origin, `credentials: "same-origin"`
 * - every non-GET request to /api (except /api/auth/*) sends `X-Carno-CSRF`
 * - errors become `ApiError` with the server's plain `message`; raw codes are
 *   for logic only and are never shown to people.
 */
import type { ErrorCode } from "@carno/shared/errors";
import type { AppSummary as SharedAppSummary, DataFilter, GeneralLevel, Person as SharedPerson, Role, ShareRole } from "@carno/shared/schemas";
import { errors as errorCopy } from "../copy/errors";
import { reportNetworkFailure, reportNetworkSuccess } from "./online";

// ---------------------------------------------------------------------------
// Contract types
// ---------------------------------------------------------------------------

export type Person = SharedPerson;
export type { DataFilter, GeneralLevel, Role, ShareRole };
export type Theme = "system" | "light" | "dark";

export interface Me {
  user: { id: string; name: string; email: string; image: string | null };
  org: { id: string; name: string; domain: string; role: string } | null;
  prefs: { theme: Theme };
  csrfToken: string;
  onboarded: boolean;
  signIn: { google: boolean; microsoft: boolean };
}

export interface AuthOptions {
  google: boolean;
  microsoft: boolean;
}

export type AppSummary = SharedAppSummary;
export type LiveState = AppSummary["state"];
export type AppsTab = "recent" | "owned" | "shared" | "trash";

export interface Page<T> {
  items: T[];
  /** Absent on some list routes (e.g. versions today); treated as "no more". */
  nextCursor?: string | null;
}

export interface LatestFailed {
  sentence: string;
  diagnosis: string;
  fixMessage: string;
  logExcerpt: string;
}

export interface AppStatus {
  state: LiveState;
  liveVersionId: string | null;
  lastBuild?: { status: string; diagnosis?: string; sentence?: string; at: string };
  latestFailed?: LatestFailed | null;
}

export interface AppDetail extends AppSummary {
  status: AppStatus;
  details: {
    createdAt: string;
    kindLabel: string;
    framework: string;
    frameworkLabel: string;
    reasons: string[];
    technical?: { start?: string; port?: number; lastBuildAt?: string };
  };
  sharing: { general: GeneralLevel };
  usesData: boolean;
  dataSource: "app_db" | "postgres" | "sqlite" | "none";
  needsKeys: number;
}

export interface SharePerson {
  shareId: string;
  person: Person | null;
  email: string;
  role: ShareRole;
  pending: boolean;
}

export interface Sharing {
  owner: Person;
  people: SharePerson[];
  general: { level: GeneralLevel; role: ShareRole; orgName: string | null; linkAllowed: boolean };
  canShare: boolean;
  url: string;
}

export interface Version {
  id: string;
  number: number;
  note: string;
  author: Person;
  authorClient: string | null;
  source: string;
  restoredFrom: { id: string; publishedAt: string } | null;
  publishedAt: string;
  isLive: boolean;
}

export type DraftStatus = "open" | "published" | "discarded" | "expired";

export interface Draft {
  id: string;
  appId: string;
  slug?: string;
  appName?: string;
  status: DraftStatus;
  note: string;
  baseVersionId: string;
  /** The live version when this was read; sent back as expectedLiveVersionId. */
  liveVersionId?: string | null;
  liveChanged: boolean;
  preview: {
    state: "building" | "starting" | "ready" | "couldnt_start";
    url?: string | null;
    diagnosis?: string | null;
    sentence?: string | null;
    /** Ready-made "Fix it with your AI" message for a preview that couldn't start. */
    fixMessage?: string | null;
  };
}

export interface SecretInfo {
  name: string;
  label: string;
  isSet: boolean;
  masked: string | null;
  required: boolean;
  includeInPreviews: boolean;
  includeInBuilds: boolean;
  detectedFrom: string | null;
  updatedAt: string | null;
}

export type DataSource = "app_db" | "postgres" | "sqlite" | "none";

export interface FieldSummary {
  name: string;
  types: string[];
  counts?: Record<string, number>;
}

export interface CollectionInfo {
  name: string;
  count: number;
  fields: FieldSummary[];
}

export interface DataInfo {
  source: DataSource;
  collections: CollectionInfo[];
}

export interface DataColumn {
  key: string;
  type: string;
}

export interface DataRow {
  id: string;
  data: Record<string, unknown>;
  createdBy: Person;
  createdAt: string;
}

export interface RowsPage {
  columns: DataColumn[];
  rows: DataRow[];
  nextCursor: string | null;
  total: number;
}

export interface RowsQuery {
  q?: string;
  sort?: { field: string; dir: "asc" | "desc" } | null;
  filters?: DataFilter[];
  cursor?: string | null;
  limit?: number;
}

export type UploadState = "processing" | "needs_review" | "needs_keys" | "starting" | "live" | "rejected" | "couldnt_start";

/** Something in the files that looks like a password or key (a review step, not a block). */
export interface UploadFinding {
  path: string;
  line: number;
  /** Plain kind, e.g. "OpenAI key", "Password or key in code". */
  kind: string;
}

export interface UploadNeed {
  name: string;
  label: string;
  reason: string;
}

export type UploadStage = "uploading" | "unpacking" | "checking" | "installing" | "building" | "publishing" | "starting" | "verifying" | "live";

/** Plain-language server progress for an upload (GET /api/uploads/:id and SSE upload.progress). */
export interface UploadProgress {
  stage: UploadStage;
  label: string;
  /** Overall percent of the server's work, 0–100. */
  pct: number;
  steps: { stage: UploadStage; label: string; state: "done" | "active" | "todo" }[];
  startedAt: string;
  etaSeconds: number | null;
  detail: string | null;
}

export interface UploadStatus {
  status: UploadState;
  appId?: string | null;
  slug?: string | null;
  name?: string | null;
  rejectCode?: ErrorCode | null;
  rejectPath?: string | null;
  /** Plain sentence from the server (why it was rejected / couldn't start). */
  message?: string | null;
  needs?: UploadNeed[] | null;
  /** needs_review: what looked like a password or key (≤ 20). */
  findings?: UploadFinding[] | null;
  estimate: "seconds" | "minute" | "minutes";
  diagnosis?: string | null;
  progress?: UploadProgress | null;
}

export interface CreatedUpload {
  uploadId: string;
  putUrl: string;
  headers: Record<string, string>;
  expiresAt: string;
}

export interface AiConnection {
  clientId: string;
  clientName: string;
  clientHost: string;
  firstSeenAt: string;
  lastSeenAt: string;
}

export interface ConnectInfo {
  connectorUrl: string;
  cliCommand: string;
}

export interface DeletionPlan {
  ownedApps: { id: string; name: string; editors: { id: string; name: string }[] }[];
}

export interface ClientInfo {
  name: string;
  host: string;
  verified: boolean;
  registeredAutomatically: boolean;
}

export type BillingPlan = "free" | "pro";

export interface Billing {
  plan: BillingPlan;
  status: string;
  hasPaymentMethod: boolean;
  delinquent: boolean;
  cardLast4: string | null;
  cardBrand: string | null;
  currentPeriodEnd: string | null;
  limits: { maxApps: number; maxBuildsPerDay: number };
  billingEnabled: boolean;
  hasCustomer: boolean;
  publishableKey: string | null;
  csrfToken: string;
}

export type CheckoutMode = "setup" | "subscription";

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/** Codes the client adds on top of the server's ErrorCode list. */
export type ClientErrorCode = ErrorCode | "NETWORK" | "UNAVAILABLE" | "UNKNOWN";

export class ApiError extends Error {
  readonly status: number;
  readonly code: ClientErrorCode;
  readonly requestId: string | null;
  readonly details: Record<string, unknown> | undefined;

  constructor(opts: {
    status: number;
    code: ClientErrorCode;
    message: string;
    requestId?: string | null;
    details?: Record<string, unknown> | undefined;
  }) {
    super(opts.message);
    this.name = "ApiError";
    this.status = opts.status;
    this.code = opts.code;
    this.requestId = opts.requestId ?? null;
    this.details = opts.details;
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

/** Statuses that mean "Carno is briefly unavailable" (restart, deploy of our own servers, proxy hiccup). */
const UNAVAILABLE_STATUSES = new Set([502, 503, 504]);

/**
 * True for failures that fix themselves: no network, or Carno briefly
 * unavailable. These are retried quietly with a "Reconnecting…" banner and
 * never shown as "Something went wrong".
 */
export function isTransientError(e: unknown): boolean {
  return isApiError(e) && (e.code === "NETWORK" || e.code === "UNAVAILABLE" || UNAVAILABLE_STATUSES.has(e.status));
}

/** The plain sentence to show for any thrown value. Never a raw code. */
export function errorMessage(e: unknown, fallback: string = errorCopy.generic): string {
  if (isApiError(e) && e.message.trim() !== "") return e.message;
  return fallback;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function parseErrorBody(status: number, body: unknown): ApiError {
  if (isRecord(body) && isRecord(body.error)) {
    const e = body.error;
    const code = typeof e.code === "string" ? (e.code as ClientErrorCode) : "UNKNOWN";
    const message = typeof e.message === "string" && e.message !== "" ? e.message : errorCopy.generic;
    const requestId = typeof e.requestId === "string" ? e.requestId : null;
    const details = isRecord(e.details) ? e.details : undefined;
    return new ApiError({ status, code, message, requestId, details });
  }
  return new ApiError({ status, code: status === 401 ? "UNAUTHENTICATED" : "UNKNOWN", message: errorCopy.generic });
}

// ---------------------------------------------------------------------------
// Transport (replaceable by the dev-only mock; see src/mocks)
// ---------------------------------------------------------------------------

export interface PutProgress {
  loaded: number;
  total: number;
}

export interface EventSourceLike {
  addEventListener(type: string, listener: (ev: MessageEvent<string>) => void): void;
  close(): void;
  onopen: ((ev: Event) => void) | null;
  onerror: ((ev: Event) => void) | null;
  readonly readyState: number;
}

export interface Transport {
  fetch: (input: string, init: RequestInit) => Promise<Response>;
  /** PUT a file with upload progress (XMLHttpRequest; fetch has no upload progress). */
  put: (url: string, headers: Record<string, string>, body: Blob, onProgress: (p: PutProgress) => void, signal: AbortSignal) => Promise<void>;
  /** Start a file download the browser saves. */
  download: (url: string, filename: string) => void;
  eventSource: (url: string) => EventSourceLike;
}

function xhrPut(url: string, headers: Record<string, string>, body: Blob, onProgress: (p: PutProgress) => void, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    for (const [name, value] of Object.entries(headers)) xhr.setRequestHeader(name, value);
    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) onProgress({ loaded: ev.loaded, total: ev.total });
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new ApiError({ status: xhr.status, code: "UNKNOWN", message: errorCopy.uploadFailed }));
    };
    xhr.onerror = () => reject(new ApiError({ status: 0, code: "NETWORK", message: errorCopy.uploadNetwork }));
    xhr.onabort = () => reject(new DOMException("Aborted", "AbortError"));
    signal.addEventListener("abort", () => xhr.abort(), { once: true });
    xhr.send(body);
  });
}

function anchorDownload(url: string, filename: string): void {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.append(a);
  a.click();
  a.remove();
}

const transport: Transport = {
  fetch: (input, init) => window.fetch(input, init),
  put: xhrPut,
  download: anchorDownload,
  eventSource: (url) => new EventSource(url, { withCredentials: true }),
};

/** Used only by the dev mock and tests. */
export function configureTransport(overrides: Partial<Transport>): void {
  Object.assign(transport, overrides);
}

export function getTransport(): Transport {
  return transport;
}

// ---------------------------------------------------------------------------
// Request core
// ---------------------------------------------------------------------------

let csrfToken: string | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
type QueryValue = string | number | boolean | null | undefined;

interface RequestOptions {
  body?: unknown;
  query?: Record<string, QueryValue>;
  signal?: AbortSignal | undefined;
}

export function buildPath(path: string, query?: Record<string, QueryValue>): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === "") continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs === "" ? path : `${path}?${qs}`;
}

function needsCsrf(method: Method, path: string): boolean {
  return method !== "GET" && path.startsWith("/api/") && !path.startsWith("/api/auth/");
}

async function send(method: Method, path: string, opts: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";
  if (needsCsrf(method, path) && csrfToken !== null) headers["X-Carno-CSRF"] = csrfToken;
  const init: RequestInit = { method, headers, credentials: "same-origin" };
  if (opts.body !== undefined) init.body = JSON.stringify(opts.body);
  if (opts.signal) init.signal = opts.signal;
  try {
    const res = await transport.fetch(buildPath(path, opts.query), init);
    if (UNAVAILABLE_STATUSES.has(res.status)) reportNetworkFailure();
    else reportNetworkSuccess();
    return res;
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") throw e;
    reportNetworkFailure();
    throw new ApiError({ status: 0, code: "NETWORK", message: errorCopy.network });
  }
}

async function readBody(res: Response): Promise<unknown> {
  if (res.status === 204) return undefined;
  const text = await res.text();
  if (text === "") return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return undefined;
  }
}

/**
 * Low-level request. Retries a mutating request once when the CSRF token went
 * stale (e.g. after the session rotated in another tab).
 */
export async function request<T>(method: Method, path: string, opts: RequestOptions = {}): Promise<T> {
  let res = await send(method, path, opts);
  if (res.status === 403 && needsCsrf(method, path)) {
    const peek = parseErrorBody(res.status, await readBody(res.clone()));
    if (peek.code === "CSRF_FAILED") {
      await getMe();
      res = await send(method, path, opts);
    }
  }
  const body = await readBody(res);
  if (UNAVAILABLE_STATUSES.has(res.status)) {
    // Proxy pages (HTML) or our own envelope: either way, "briefly unavailable".
    const parsed = parseErrorBody(res.status, body);
    throw new ApiError({ status: res.status, code: "UNAVAILABLE", message: errorCopy.reconnecting, requestId: parsed.requestId });
  }
  if (!res.ok) throw parseErrorBody(res.status, body);
  return body as T;
}

const enc = encodeURIComponent;

/** base64url of a UTF-8 JSON string (Data view `filter` param). */
export function encodeFilter(filters: DataFilter[]): string {
  const bytes = new TextEncoder().encode(JSON.stringify(filters));
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** The same filters as on screen, but the whole list (no page cursor or limit). */
function exportQuery(query: RowsQuery): RowsQuery {
  const out: RowsQuery = {};
  if (query.q !== undefined) out.q = query.q;
  if (query.sort !== undefined) out.sort = query.sort;
  if (query.filters !== undefined) out.filters = query.filters;
  return out;
}

function rowsParams(query: RowsQuery): Record<string, QueryValue> {
  return {
    q: query.q,
    sort: query.sort ? `${query.sort.field}:${query.sort.dir}` : undefined,
    filter: query.filters && query.filters.length > 0 ? encodeFilter(query.filters) : undefined,
    cursor: query.cursor,
    limit: query.limit,
  };
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export const api = {
  // Me & account ------------------------------------------------------------
  async getMe(signal?: AbortSignal): Promise<Me> {
    return getMe(signal);
  },
  async updateMe(patch: { name?: string; prefs?: { theme?: Theme } }): Promise<Me> {
    const me = await request<Me>("PATCH", "/api/me", { body: patch });
    setCsrfToken(me.csrfToken);
    return me;
  },
  getAuthOptions: (signal?: AbortSignal) => request<AuthOptions>("GET", "/api/auth-options", { signal }),
  getConnect: (signal?: AbortSignal) => request<ConnectInfo>("GET", "/api/connect", { signal }),
  listAiConnections: (signal?: AbortSignal) => request<{ items: AiConnection[] }>("GET", "/api/me/ai-connections", { signal }),
  disconnectAi: (clientId: string) => request<void>("DELETE", `/api/me/ai-connections/${enc(clientId)}`),
  exportMyData: () => request<{ jobId: string }>("POST", "/api/me/export"),
  getDeletionPlan: (signal?: AbortSignal) => request<DeletionPlan>("GET", "/api/me/deletion-plan", { signal }),
  deleteMe: (body: { confirmEmail: string; transfers: { appId: string; toUserId: string }[] }) => request<void>("DELETE", "/api/me", { body }),

  // Billing -------------------------------------------------------------------
  getBilling: (signal?: AbortSignal) => request<Billing>("GET", "/api/billing", { signal }),
  startCheckout: (mode: CheckoutMode) => request<{ url: string }>("POST", "/api/billing/checkout", { body: { mode } }),
  openBillingPortal: () => request<{ url: string }>("POST", "/api/billing/portal", { body: {} }),

  // Apps ----------------------------------------------------------------------
  listApps: (params: { tab: AppsTab; q?: string; cursor?: string | null; limit?: number }, signal?: AbortSignal) =>
    request<Page<AppSummary>>("GET", "/api/apps", {
      query: { tab: params.tab, q: params.q, cursor: params.cursor, limit: params.limit },
      signal,
    }),
  getApp: (appId: string, signal?: AbortSignal) => request<AppDetail>("GET", `/api/apps/${enc(appId)}`, { signal }),
  getAppBySlug: (slug: string, signal?: AbortSignal) => request<AppDetail>("GET", `/api/apps/by-slug/${enc(slug)}`, { signal }),
  renameApp: (appId: string, name: string) => request<AppSummary>("PATCH", `/api/apps/${enc(appId)}`, { body: { name } }),
  trashApp: (appId: string) => request<void>("POST", `/api/apps/${enc(appId)}/trash`),
  untrashApp: (appId: string) => request<void>("POST", `/api/apps/${enc(appId)}/untrash`),
  untrashBySlug: (slug: string) => request<void>("POST", `/api/apps/by-slug/${enc(slug)}/untrash`),
  deleteAppForever: (appId: string) => request<void>("DELETE", `/api/apps/${enc(appId)}`, { body: { confirm: true } }),
  hideApp: (appId: string) => request<void>("POST", `/api/apps/${enc(appId)}/hide`),
  transferApp: (appId: string, toUserId: string) => request<void>("POST", `/api/apps/${enc(appId)}/transfer`, { body: { toUserId } }),
  recordOpened: (appId: string) => request<void>("POST", `/api/apps/${enc(appId)}/opened`, { body: {} }),
  heartbeat: (appId: string) => request<void>("POST", `/api/apps/${enc(appId)}/presence`, { body: {} }),
  downloadAppUrl: (appId: string) => `/api/apps/${enc(appId)}/download`,

  // Sharing -------------------------------------------------------------------
  getSharing: (appId: string, signal?: AbortSignal) => request<Sharing>("GET", `/api/apps/${enc(appId)}/sharing`, { signal }),
  addShares: (appId: string, body: { emails: string[]; role: ShareRole; notify: boolean; message?: string }) =>
    request<Sharing>("POST", `/api/apps/${enc(appId)}/shares`, { body }),
  updateShare: (appId: string, shareId: string, role: ShareRole) =>
    request<Sharing>("PATCH", `/api/apps/${enc(appId)}/shares/${enc(shareId)}`, { body: { role } }),
  removeShare: (appId: string, shareId: string) => request<Sharing>("DELETE", `/api/apps/${enc(appId)}/shares/${enc(shareId)}`),
  setGeneralAccess: (appId: string, level: GeneralLevel, role: ShareRole) =>
    request<Sharing>("PUT", `/api/apps/${enc(appId)}/general-access`, { body: { level, role } }),
  suggestPeople: (q: string, signal?: AbortSignal) => request<{ items: Person[] }>("GET", "/api/people/suggest", { query: { q }, signal }),
  requestAccess: (slug: string, message: string) =>
    request<void>("POST", "/api/access-requests", { body: message.trim() === "" ? { slug } : { slug, message: message.trim() } }),

  // Versions ------------------------------------------------------------------
  listVersions: (appId: string, cursor: string | null, signal?: AbortSignal) =>
    request<Page<Version>>("GET", `/api/apps/${enc(appId)}/versions`, { query: { cursor, limit: 50 }, signal }),
  restoreVersion: (appId: string, versionId: string, expectedLiveVersionId: string | null) =>
    request<{ versionId: string; liveVersionId: string }>("POST", `/api/apps/${enc(appId)}/versions/${enc(versionId)}/restore`, {
      body: { expectedLiveVersionId },
    }),
  viewVersion: (appId: string, versionId: string) =>
    request<{ previewUrl: string; draftId?: string }>("POST", `/api/apps/${enc(appId)}/versions/${enc(versionId)}/view`, { body: {} }),

  // Drafts --------------------------------------------------------------------
  getDraft: (draftId: string, signal?: AbortSignal) => request<Draft>("GET", `/api/drafts/${enc(draftId)}`, { signal }),
  publishDraft: (draftId: string, expectedLiveVersionId: string | null) =>
    request<{ versionId: string; liveVersionId: string }>("POST", `/api/drafts/${enc(draftId)}/publish`, {
      body: { expectedLiveVersionId },
    }),
  discardDraft: (draftId: string) => request<void>("POST", `/api/drafts/${enc(draftId)}/discard`),
  reopenDraft: (draftId: string) => request<void>("POST", `/api/drafts/${enc(draftId)}/reopen`),

  // Keys & passwords ------------------------------------------------------------
  listSecrets: (appId: string, signal?: AbortSignal) => request<{ items: SecretInfo[] }>("GET", `/api/apps/${enc(appId)}/secrets`, { signal }),
  putSecret: (appId: string, name: string, body: { value: string; label?: string; includeInPreviews?: boolean }) =>
    request<SecretInfo>("PUT", `/api/apps/${enc(appId)}/secrets/${enc(name)}`, { body }),
  patchSecret: (appId: string, name: string, body: { includeInPreviews?: boolean }) =>
    request<SecretInfo>("PATCH", `/api/apps/${enc(appId)}/secrets/${enc(name)}`, { body }),

  // Data view -------------------------------------------------------------------
  getData: (appId: string, signal?: AbortSignal) => request<DataInfo>("GET", `/api/apps/${enc(appId)}/data`, { signal }),
  getRows: (appId: string, collection: string, query: RowsQuery, signal?: AbortSignal) =>
    request<RowsPage>("GET", `/api/apps/${enc(appId)}/data/${enc(collection)}/rows`, { query: rowsParams(query), signal }),
  deleteRows: (appId: string, collection: string, ids: string[]) =>
    request<{ deleted: number; undoToken: string }>("POST", `/api/apps/${enc(appId)}/data/${enc(collection)}/delete`, {
      body: { ids },
    }),
  undeleteRows: (appId: string, collection: string, undoToken: string) =>
    request<void>("POST", `/api/apps/${enc(appId)}/data/${enc(collection)}/undelete`, { body: { undoToken } }),
  exportUrl: (appId: string, collection: string, format: "csv" | "xlsx", query: RowsQuery) =>
    buildPath(`/api/apps/${enc(appId)}/data/${enc(collection)}/export`, {
      format,
      ...rowsParams(exportQuery(query)),
    }),

  // Uploads ---------------------------------------------------------------------
  createUpload: (body: { filename: string; size: number; contentType: string }) => request<CreatedUpload>("POST", "/api/uploads", { body }),
  completeUpload: (uploadId: string, name: string | undefined) =>
    request<{ uploadId: string }>("POST", `/api/uploads/${enc(uploadId)}/complete`, {
      body: name === undefined ? {} : { name },
    }),
  getUpload: (uploadId: string, signal?: AbortSignal) => request<UploadStatus>("GET", `/api/uploads/${enc(uploadId)}`, { signal }),
  continueUpload: (uploadId: string) => request<void>("POST", `/api/uploads/${enc(uploadId)}/continue`, { body: {} }),
  /** "It's not a real password — continue" after a needs_review. */
  acknowledgeUpload: (uploadId: string) => request<void>("POST", `/api/uploads/${enc(uploadId)}/acknowledge`, { body: {} }),
  /**
   * "Try again" for an upload that couldn't start: re-checks the stored files and
   * starts the same app again (no re-upload, no new app). 202 with the upload view;
   * 400 VALIDATION_FAILED (plain message) unless it's couldnt_start with an app.
   */
  retryUpload: (uploadId: string) => request<UploadStatus>("POST", `/api/uploads/${enc(uploadId)}/retry`, { body: {} }),
  /** The last lines an app printed while getting ready (build) or running (API.md §2.6 logs). */
  getLogs: (appId: string, kind: "build" | "runtime", signal?: AbortSignal) =>
    request<{ lines: { at: string | null; stream: string; text: string }[] }>("GET", `/api/apps/${enc(appId)}/logs?kind=${kind}&limit=200`, { signal }),

  // OAuth consent -----------------------------------------------------------------
  getClientInfo: (clientId: string, signal?: AbortSignal) => request<ClientInfo>("GET", "/api/oauth/client-info", { query: { client_id: clientId }, signal }),

  // Realtime ------------------------------------------------------------------------
  eventsUrl: "/api/events",
} as const;

async function getMe(signal?: AbortSignal): Promise<Me> {
  const me = await request<Me>("GET", "/api/me", { signal });
  setCsrfToken(me.csrfToken);
  return me;
}
